import React, { useState, memo } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { PrimaryButtonInline } from '../../../components';
import { campaignListingsSelector, closeListing } from '../ListingPage.duck';
import { isArrayLength } from '../../../util/genericHelpers';
import { formatMoney } from '../../../util/currency';

import css from './SharedComponents.module.css';
import '../../../styles/slick/slick.css';
import '../../../styles/slick/slick-theme.css';
import { useListingPageContext } from '../context/ListingPageContext';
import ListingPageActionButtons from '../ListingPageActionButtons';
import { CAMPAIGN, EDIT } from '../../../util/constants';
import { createResourceLocatorString } from '../../../util/routes';
import { createSlug } from '../../../util/urlHelpers';

// Tab Component
const Tab = memo(({ label, isActive, onClick }) => (
  <span onClick={onClick} className={classNames(css.tab, { [css.active]: isActive })}>
    {label}
  </span>
));

// DataGroup Component
const DataGroup = memo(({ data }) => (
  <div className={css.tabContent}>
    {data?.map((item, index) => (
      <div className={css.tabData} key={item.id || index}>
        {item.description}
      </div>
    ))}
  </div>
));

// Tabs Component
const Tabs = memo(
  ({ description, publicData = {}, formattedMoney, isCurrentUserLinkedToListing }) => {
    const [activeTab, setActiveTab] = useState(0);
    const { tasks = [], perkProvided = '' } = publicData;

    const tabConfig = [
      {
        label: 'Description',
        content: [{ id: 'desc', description: <p>{description}</p> }],
      },
      {
        label: 'Requirements',
        content: [
          {
            id: 'req',
            description: isArrayLength(tasks) ? (
              <ul>
                {tasks.map((task, index) => (
                  <li key={`${index}_${task}`}>{task}</li>
                ))}
              </ul>
            ) : (
              <p>No requirements specified</p>
            ),
          },
        ],
      },
      {
        label: 'Compensation',
        content: [
          {
            id: 'comp',
            description: (
              <p>{`${formattedMoney}${perkProvided ? `, ${perkProvided.trim()}` : ''}`}</p>
            ),
          },
        ],
      },
      // Only include Apply Now tab if user is linked to listing
      ...(isCurrentUserLinkedToListing
        ? [
            {
              label: 'Apply Now',
              content: [
                {
                  id: 'apply',
                  description: (
                    <PrimaryButtonInline className={css.applyBtn}>Apply Now</PrimaryButtonInline>
                  ),
                },
              ],
            },
          ]
        : []),
    ];

    return (
      <div className={css.agencyCampaignsTabs}>
        <div className={css.tabs}>
          {tabConfig.map((tab, index) => (
            <Tab
              key={tab.label}
              label={tab.label}
              isActive={activeTab === index}
              onClick={() => setActiveTab(index)}
            />
          ))}
        </div>
        <DataGroup data={tabConfig[activeTab].content} />
      </div>
    );
  }
);

// Step Component
const Step = memo(
  ({
    listing,
    intl,
    isOwnListing,
    dispatch,
    history,
    routeConfiguration,
    profileImageIcon,
    isCurrentUserLinkedToListing,
  }) => {
    const { title, description, publicData = {}, price } = listing?.attributes || {};
    const formattedMoney = price ? formatMoney(intl, price) : 'N/A';
    const perkProvided = publicData.perkProvided || '';
    const handleEdit = () => {
      history.push({
        pathname: createResourceLocatorString(
          'EditCampaignPostingPage',
          routeConfiguration,
          { id: listing?.id?.uuid, slug: createSlug(title), type: EDIT },
          {}
        ),
        state: { isEditCampaignPostPage: true },
      });
    };

    const handleDelete = async () => {
      await dispatch(closeListing(CAMPAIGN, listing?.id?.uuid));
    };
    return (
      <div className={css.couponBlock}>
        <div className={css.blockTopInfo}>
          <div className={css.offerNameSec}>
            <div className={css.offerImgSec}>
              <img src={profileImageIcon} alt={title || 'Campaign image'} loading="lazy" />
            </div>
            <div className={css.offerInfoSec}>
              <h3>{title || 'Untitled Campaign'}</h3>
            </div>
          </div>
          <div className={css.pills}>
            <span className={css.pill}>
              {`${formattedMoney}${perkProvided ? `, ${perkProvided.trim()}` : ''}`}
            </span>
          </div>
          <Tabs
            description={description}
            publicData={publicData}
            formattedMoney={formattedMoney}
            isCurrentUserLinkedToListing={isCurrentUserLinkedToListing}
          />
        </div>
        {isOwnListing && (
          <ListingPageActionButtons
            listingId={listing?.id?.uuid}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
          />
        )}
      </div>
    );
  }
);

// Main Component
const RenderCampaigns = ({ intl, profileImageIcon, isCurrentUserLinkedToListing }) => {
  const dispatch = useDispatch();
  const campaignListings = useSelector(campaignListingsSelector);
  const { isOwnListing, history, routeConfiguration } = useListingPageContext();

  if (!isArrayLength(campaignListings)) {
    return <></>;
  }

  return (
    <div className={classNames(css.talentOptions, css.brandPostings)}>
      <div className={css.contentWidth}>
        <div className={css.sectionHead}>
          <div className={css.leftSec}>
            <h2>
              <FormattedMessage id="RenderCampaigns.title" />
            </h2>
          </div>
        </div>
        <div className={css.brandCampaignsSec}>
          {campaignListings.map(listing => (
            <Step
              key={listing?.id?.uuid || Math.random()}
              intl={intl}
              listing={listing}
              isOwnListing={isOwnListing}
              dispatch={dispatch}
              history={history}
              profileImageIcon={profileImageIcon}
              routeConfiguration={routeConfiguration}
              isCurrentUserLinkedToListing={isCurrentUserLinkedToListing}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

RenderCampaigns.defaultProps = {
  sliderSettings: {
    dots: false,
    arrows: true,
    infinite: true,
    autoplay: false,
    autoplaySpeed: 2000,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: false,
    focusOnSelect: false,
    centerMode: false,
    responsive: [
      { breakpoint: 1128, settings: { slidesToShow: 1, variableWidth: false } },
      { breakpoint: 991, settings: { slidesToShow: 1, variableWidth: false } },
      { breakpoint: 767, settings: { centerMode: true, slidesToShow: 1, variableWidth: false } },
      { breakpoint: 480, settings: { centerMode: true, slidesToShow: 1, variableWidth: false } },
    ],
  },
};

export default memo(RenderCampaigns);
